/*
 * Copyright 2024 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import fs from "node:fs";
import path from "node:path";
import { fileURLToPath } from "node:url";
import { changeVersionPrefix } from "@osdk/generator-utils";
import { findUpSync } from "find-up";
import Handlebars from "handlebars";
import { consola } from "./consola.js";
import { generateAuthlessEnvDevelopment, generateAuthlessEnvProduction, generateEnvDevelopment, generateEnvProduction } from "./generate/generateEnv.js";
import { generateFoundryConfigJson } from "./generate/generateFoundryConfigJson.js";
import { generateNpmRc } from "./generate/generateNpmRc.js";
import { green } from "./highlight.js";
export async function run({
  project,
  overwrite,
  template,
  sdkVersion,
  foundryUrl,
  applicationUrl,
  application,
  ontology,
  clientId,
  osdkPackage,
  osdkRegistryUrl,
  corsProxy,
  scopes,
  unstableFeatures = false
}) {
  consola.log("");
  consola.start(`Creating project ${green(project)} using template ${green(template.id)}`);
  const isAuthless = template.authless ?? false;
  const cwd = process.cwd();
  const root = path.join(cwd, project);
  if (fs.existsSync(root)) {
    if (overwrite) {
      consola.info(`Overwriting existing project directory`);
      fs.rmSync(root, {
        recursive: true,
        force: true
      });
      fs.mkdirSync(root, {
        recursive: true
      });
    } else {
      consola.info(`Reusing existing project directory`);
    }
  } else {
    consola.info(`Creating project directory`);
    fs.mkdirSync(root, {
      recursive: true
    });
  }
  consola.info(`Copying files into project directory`);
  if (template.files[sdkVersion] == null) {
    throw new Error(`The ${template.label} template does not support a "${sdkVersion}" SDK version.`);
  }
  const files = await template.files[sdkVersion]();
  for (const [filePath, contents] of files) {
    const finalPath = path.join(root, filePath);
    const dirPath = path.dirname(finalPath);
    await fs.promises.mkdir(dirPath, {
      recursive: true
    });
    await fs.promises.writeFile(finalPath, Buffer.from(contents.body, contents.type === "raw" ? "utf-8" : "base64"));
  }
  const ourPackageJsonPath = findUpSync("package.json", {
    cwd: fileURLToPath(import.meta.url)
  });
  const ourPackageJsonVersion = ourPackageJsonPath ? JSON.parse(fs.readFileSync(ourPackageJsonPath, "utf-8")).version : undefined;
  const clientVersion = process.env.PACKAGE_CLIENT_VERSION ?? ourPackageJsonVersion;
  if (clientVersion === undefined) {
    throw new Error("Could not determine current @osdk/client version");
  }
  const templateContext = {
    application,
    applicationUrl,
    project,
    foundryUrl,
    osdkPackage,
    corsProxy,
    clientVersion: changeVersionPrefix(clientVersion, "^"),
    scopes,
    unstableFeatures,
    clientId
  };
  const processFiles = function (dir) {
    fs.readdirSync(dir).forEach(file => {
      let fullPath = dir + "/" + file;
      const stat = fs.statSync(fullPath);
      if (stat.isDirectory()) {
        processFiles(fullPath);
        return;
      }
      if (fullPath.endsWith("/_gitignore")) {
        fs.renameSync(fullPath, fullPath.replace(/\/_gitignore$/u, "/.gitignore"));
        return;
      }

      // Files with the `.osdk` extension are only kept if the application uses an OSDK
      if (file.includes(".osdk")) {
        if (osdkPackage == null) {
          fs.rmSync(fullPath);
          return;
        } else {
          fs.renameSync(fullPath, fullPath.replace(".osdk", ""));
          fullPath = fullPath.replace(".osdk", "");
        }
        // Files with the `.psdk` extension are only kept if the application does not use an OSDK
      } else if (file.includes(".psdk")) {
        if (osdkPackage == null) {
          fs.renameSync(fullPath, fullPath.replace(".psdk", ""));
          fullPath = fullPath.replace(".psdk", "");
        } else {
          fs.rmSync(fullPath);
          return;
        }
      }
      if (!fullPath.endsWith(".hbs")) {
        return;
      }
      const templated = Handlebars.compile(fs.readFileSync(fullPath, "utf-8"))(templateContext);
      fs.writeFileSync(fullPath.replace(/.hbs$/u, ""), templated);
      fs.rmSync(fullPath);
    });
  };
  processFiles(root);
  if (unstableFeatures && osdkPackage != null) {
    const packageJsonPath = path.join(root, "package.json");
    const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, "utf-8"));
    packageJson.scripts = {
      ...packageJson.scripts,
      postinstall: "./node_modules/.bin/osdk unstable branch sync"
    };
    packageJson.devDependencies = {
      "@osdk/cli": "latest",
      ...packageJson.devDependencies
    };
    if (fs.existsSync(path.join(root, "vite.config.ts"))) {
      packageJson.dependencies = {
        "@osdk/vite-plugin-branch": changeVersionPrefix(clientVersion, "^"),
        ...packageJson.dependencies
      };
    }
    fs.writeFileSync(packageJsonPath, JSON.stringify(packageJson, null, 2));
  }
  const npmRc = generateNpmRc({
    osdkPackage,
    osdkRegistryUrl,
    foundryUrl
  });
  fs.writeFileSync(path.join(root, ".npmrc"), npmRc);
  const envDevelopment = isAuthless ? generateAuthlessEnvDevelopment({
    envPrefix: template.envPrefix,
    ontology
  }) : generateEnvDevelopment({
    envPrefix: template.envPrefix,
    foundryUrl,
    clientId: clientId ?? "",
    corsProxy,
    ontology
  });
  fs.writeFileSync(path.join(root, ".env.development"), envDevelopment);
  const envProduction = isAuthless ? generateAuthlessEnvProduction({
    envPrefix: template.envPrefix,
    applicationUrl,
    ontology
  }) : generateEnvProduction({
    envPrefix: template.envPrefix,
    foundryUrl,
    applicationUrl,
    clientId: clientId ?? "",
    ontology
  });
  fs.writeFileSync(path.join(root, ".env.production"), envProduction);
  const foundryConfigJson = generateFoundryConfigJson({
    foundryUrl,
    application,
    directory: template.buildDirectory
  });
  fs.writeFileSync(path.join(root, "foundry.config.json"), foundryConfigJson);
  consola.success("Success");
  const cdRelative = path.relative(cwd, root);
  consola.box({
    message: `Done! Run the following commands to get started:\n` + `\n` + `  \`cd ${cdRelative}\`\n` + `  \`export FOUNDRY_TOKEN=<token>\`\n` + `  \`npm install\`\n` + `  \`npm run dev\``,
    style: {
      padding: 2,
      borderColor: "green",
      borderStyle: "rounded"
    }
  });
}